/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

'use client';

import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@airasia-rn/error-boundary';
import { fireGtmEvent } from '@airasia-rn/ui';

export interface HomeErrorBoundaryProps {
  children: ReactNode;
  locale?: string;
  currency?: string;
  geoId?: string;
  screenName?: string;
  lobName?: string;
  onError?: (error: Error, stackTrace: string) => void;
}

export const logHomeErrorGtm = (
  error: Error,
  screenName = 'HomeV2',
  lobName = 'home',
  locale?: string,
  currency?: string,
  geoId?: string,
) => {
  const errorCode =
    (error as Error & { code?: string; statusCode?: number })?.code ||
    (error as Error & { code?: string; statusCode?: number })?.statusCode ||
    '500';
  const errorDescription = error?.name || 'Internal Server Error';
  const errorMessage = error?.message || 'Something went wrong';

  fireGtmEvent({
    event: 'trackEventNoEcommerce',
    eventCategory: 'error|runtime',
    eventAction: 'errors',
    eventLabel: `${errorCode}|${errorDescription}|${errorMessage}|locale:${locale ?? ''}|currency:${currency ?? ''}|geoId:${geoId ?? ''}`,
    service: 'home-web',
    screenName,
    lineofbusiness: lobName,
    ...(locale ? { locale } : {}),
    ...(currency ? { currency } : {}),
    ...(geoId ? { geoId } : {}),
  });
};

export const HomeErrorBoundary = ({
  children,
  locale,
  currency,
  geoId,
  screenName = 'HomeV2',
  lobName = 'home',
  onError,
}: HomeErrorBoundaryProps) => {
  const [retryCount, setRetryCount] = useState(0);
  const { t } = useTranslation();

  const handleError = useCallback(
    (error: Error, stackTrace: string) => {
      logHomeErrorGtm(error, screenName, lobName, locale, currency, geoId);
      onError?.(error, stackTrace);
    },
    [screenName, lobName, locale, currency, geoId, onError],
  );

  return (
    <ErrorBoundary
      key={retryCount}
      onButtonPress={() => setRetryCount((previous) => previous + 1)}
      lobName={lobName}
      screenName={screenName}
      locale={locale}
      retryLabel={t('homePage.retry')}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
};

export default HomeErrorBoundary;
